/**
 * Type helpers to map between Database types (from Supabase) and frontend types
 * This avoids type mismatches when DB uses Json types
 */

import { Database } from '@/integrations/supabase/types'
import { Category } from './database'

type DBCategory = Database['public']['Tables']['categories']['Row']
type DBUser = Database['public']['Tables']['users']['Row']

/**
 * Map DB category row to frontend Category type
 */
export function mapDBCategoryToCategory(dbCat: DBCategory): Category {
  // Parse target_demographics from Json to string[]
  let demographics: string[] | undefined
  if (dbCat.target_demographics) {
    try {
      if (Array.isArray(dbCat.target_demographics)) {
        demographics = dbCat.target_demographics as string[]
      }
    } catch {
      demographics = undefined
    }
  }

  return {
    ...dbCat,
    target_demographics: (demographics ?? dbCat.target_demographics) as any,
  } as Category
}

/**
 * Safely coerce Json to string[]
 */
export function jsonToStringArray(json: unknown): string[] {
  if (Array.isArray(json)) {
    return json.filter(item => typeof item === 'string') as string[]
  }
  return []
}

/**
 * Ensure subscription_status is never null
 */
export function normalizeSubscriptionStatus(
  status: string | null
): 'free' | 'premium' | 'cancelled' {
  if (status === 'premium' || status === 'cancelled') return status
  return 'free'
}

/**
 * Map DB user row ensuring non-null subscription_status
 */
export function normalizeUserRow(user: DBUser): DBUser & { subscription_status: 'free' | 'premium' | 'cancelled' } {
  return {
    ...user,
    subscription_status: normalizeSubscriptionStatus(user.subscription_status)
  }
}

/**
 * Convert UserPreferences to Json-compatible format
 */
export function userPreferencesToJson(prefs: any): any {
  return prefs as any
}

/**
 * Convert Json to UserPreferences
 */
export function jsonToUserPreferences(json: any): any {
  return json || {}
}

/**
 * Convert features Json array to string[]
 */
export function jsonToFeatures(json: any): string[] {
  if (Array.isArray(json)) {
    return json.filter(item => typeof item === 'string')
  }
  return []
}
